import type { Draft, PreparedAttachment } from '../domain/capture/model';
import { CaptureService } from './capture-service';
import { errorMessage } from '../domain/shared/errors';

export interface EditorSnapshot {
  body: string; attachmentIds: string[]; status: 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'conflict';
  message: string; draftId: string; captureId: string; revision: number;
}

/** One serialized writer per editor. Captures an immutable snapshot before each await. */
export class EditorController {
  private listeners = new Set<() => void>();
  private timer?: ReturnType<typeof setTimeout>;
  private inFlight?: Promise<boolean>;
  private version = 0;
  private savedVersion = 0;
  private pendingOperation?: { version: number; id: string };
  private prepared = new Map<string, PreparedAttachment>();
  private snapshot: EditorSnapshot;
  constructor(private service: CaptureService, private baseRevision: number, draft?: Draft, captureId: string = crypto.randomUUID(), body = '', attachmentIds: string[] = []) {
    this.snapshot = {
      body: draft?.body ?? body, attachmentIds: draft?.attachmentIds ?? attachmentIds,
      draftId: draft?.id ?? crypto.randomUUID(), captureId: draft?.captureId ?? captureId,
      revision: draft?.revision ?? 0,
      status: draft ? (draft.state === 'conflict' ? 'conflict' : 'saved') : 'idle',
      message: draft?.state === 'conflict' ? '有另一份版本，双方内容均已保留。可将此草稿另存为新记录。' : draft ? '已保存在本机 · 草稿' : '只保存在你的设备上',
    };
  }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => this.listeners.delete(listener); };
  getSnapshot = () => this.snapshot;
  private set(patch: Partial<EditorSnapshot>) { this.snapshot = { ...this.snapshot, ...patch }; this.listeners.forEach((fn) => fn()); }
  private changed(patch: Partial<EditorSnapshot>) {
    this.version++;
    this.set({ ...patch, status: 'dirty', message: '等待保存…' });
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { void this.flush(); }, 500);
  }
  setBody(body: string) { this.changed({ body }); }
  addAttachment(value: PreparedAttachment) {
    this.prepared.set(value.metadata.id, value);
    this.changed({ attachmentIds: [...this.snapshot.attachmentIds, value.metadata.id] });
  }
  removeAttachment(id: string) { this.changed({ attachmentIds: this.snapshot.attachmentIds.filter((x) => x !== id) }); }
  getPrepared(id: string) { return this.prepared.get(id); }
  get isDirty() { return this.version !== this.savedVersion || this.snapshot.status === 'error' || this.snapshot.status === 'saving'; }
  async flush(): Promise<boolean> {
    clearTimeout(this.timer);
    if (this.inFlight) { const ok = await this.inFlight; return ok ? this.flush() : false; }
    if (this.version === this.savedVersion) return true;
    const version = this.version;
    const input = this.snapshot;
    if (this.pendingOperation?.version !== version) this.pendingOperation = { version, id: crypto.randomUUID() };
    const operationId = this.pendingOperation.id;
    this.set({ status: 'saving', message: '正在保存…' });
    this.inFlight = (async () => {
      try {
        const draft = await this.service.saveDraft({
          operationId, draftId: input.draftId, captureId: input.captureId,
          baseRevision: this.baseRevision, expectedDraftRevision: input.revision,
          body: input.body, attachmentIds: input.attachmentIds,
          prepared: input.attachmentIds.flatMap((id) => this.prepared.has(id) ? [this.prepared.get(id)!] : []),
        });
        this.savedVersion = version;
        this.pendingOperation = undefined;
        this.set({ draftId: draft.id, revision: draft.revision,
          status: this.version !== version ? 'dirty' : draft.state === 'conflict' ? 'conflict' : 'saved',
          message: this.version !== version ? '等待保存…' : draft.state === 'conflict' ? '有另一份版本，双方内容均已保留。可将此草稿另存为新记录。' : '已保存在本机 · 草稿',
        });
        return true;
      } catch (error) {
        this.set({ status: 'error', message: errorMessage(error) });
        return false;
      } finally { this.inFlight = undefined; }
    })();
    const ok = await this.inFlight;
    if (ok && this.version !== this.savedVersion) return this.flush();
    return ok;
  }
  ensureDraft() { if (!this.snapshot.revision && !this.version) this.version++; }
  dispose() { clearTimeout(this.timer); /* callers flush before leaving; lifecycle is not the only save path */ }
}
